import { useState } from "react";
import { PlayCircleIcon, VideoCameraIcon } from "@heroicons/react/24/outline";

type Props = {
  /** Empty string renders a "coming soon" placeholder slot. */
  youtubeId: string;
  title: string;
};

/**
 * Lazy YouTube facade: renders the static thumbnail with a play button and
 * only swaps in the real iframe on click. Keeps /guides light — a page of
 * eager YouTube iframes would load megabytes of player JS per video.
 */
export function YouTubeEmbed({ youtubeId, title }: Props) {
  const [playing, setPlaying] = useState(false);

  if (!youtubeId) {
    return (
      <div className="aspect-video w-full rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-500">
        <VideoCameraIcon className="h-8 w-8" aria-hidden="true" />
        <span className="text-sm font-medium">{title}</span>
        <span className="text-xs">Video coming soon</span>
      </div>
    );
  }

  if (playing) {
    return (
      <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
        <iframe
          className="h-full w-full"
          src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="group relative aspect-video w-full rounded-lg overflow-hidden bg-black border-none p-0 cursor-pointer"
      aria-label={`Play video: ${title}`}
    >
      <img
        src={`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`}
        alt={title}
        loading="lazy"
        className="h-full w-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
      />
      <span className="absolute inset-0 flex items-center justify-center">
        <PlayCircleIcon
          className="h-16 w-16 text-white/90 drop-shadow group-hover:scale-110 transition-transform"
          aria-hidden="true"
        />
      </span>
      <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2 text-left text-sm font-medium text-white">
        {title}
      </span>
    </button>
  );
}
